<template>
  <v-tooltip
    bottom
    :open-on-click="!interactive"
    max-width="280"
  >
    <template v-slot:activator="{ on, attrs }">
      <component
        :is="interactive ? 'button' : 'span'"
        :type="interactive ? 'button' : undefined"
        class="record-privacy"
        :class="`record-privacy--${colorState}`"
        :aria-busy="String(loading)"
        :disabled="disabled || loading"
        :aria-pressed="interactive ? String(recordEnabled) : undefined"
        :aria-label="`${status.effective_public ? 'Public' : 'Private'}: ${explanation}`"
        tabindex="0"
        v-bind="attrs"
        v-on="on"
        @click.stop="toggle"
      >
        <v-icon
          left
          small
        >
          {{ status.effective_public ? mdiEye : mdiEyeOff }}
        </v-icon>
        {{ loading ? 'Saving...' : status.effective_public ? 'Public' : 'Private' }}
      </component>
    </template>
    <span>{{ explanation }}</span>
  </v-tooltip>
</template>

<script>
import { mdiEye, mdiEyeOff } from '@mdi/js'
import {
  enabled, categoryPolicy, visibility, privacyExplanations,
} from '@/utils/recordPrivacy'

export default {
  props: {
    record: { type: Object, default: () => ({}) },
    categories: { type: Array, default: null },
    diary: { type: Boolean, default: false },
    interactive: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data: () => ({ mdiEye, mdiEyeOff }),
  computed: {
    recordEnabled() { return enabled(this.record.public) },
    status() {
      if (this.diary) return { effective_public: false, privacy_reason: 'diary' }
      const { user } = this.$store.state
      const profilePublic = user && user.extra_data && user.extra_data.public
      if (!enabled(profilePublic)) return { effective_public: false, privacy_reason: 'profile' }
      if (this.categories) return visibility(this.record, profilePublic, categoryPolicy(this.categories))
      if (!enabled(this.record.public)) return { effective_public: false, privacy_reason: 'record' }

      return {
        effective_public: enabled(this.record.effective_public),
        privacy_reason: this.record.privacy_reason || 'unknown',
      }
    },
    colorState() {
      if (this.status.effective_public) return 'public'
      if (this.recordEnabled && this.status.privacy_reason === 'category') return 'restricted'

      return 'private'
    },
    explanation() {
      const reason = privacyExplanations[this.status.privacy_reason] || privacyExplanations.unknown

      return this.interactive ? `Record setting: ${this.recordEnabled ? 'Public' : 'Private'}. ${reason} Click to change the record setting.` : reason
    },
  },
  methods: {
    toggle() {
      if (this.interactive && !this.disabled && !this.loading) this.$emit('toggle', !this.recordEnabled)
    },
  },
}
</script>

<style scoped>
.record-privacy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 7px 12px;
  border: 0;
  border-radius: 8px;
  color: #fff !important;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  vertical-align: middle;
  box-shadow: 0 2px 5px rgba(0, 0, 0, .18);
}
.record-privacy--public { background: #237a3b !important; }
.record-privacy--private { background: #c62828 !important; }
.record-privacy--restricted { background: #facc15 !important; color: #422006 !important; }
.record-privacy .v-icon { color: inherit !important; margin: 0; }
button.record-privacy { cursor: pointer; touch-action: manipulation; }
button.record-privacy:hover { filter: brightness(1.1); }
button.record-privacy:focus-visible { outline: 3px solid #fff; outline-offset: 2px; box-shadow: 0 0 0 5px #302b40; }
button.record-privacy:disabled { cursor: wait; opacity: .75; }
@media (pointer: coarse) { button.record-privacy { min-height: 44px; } }
</style>
